import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Clock, MapPin, Eye, ArrowRightLeft, Star, BadgeCheck, Calendar, Sparkles } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AnnonceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [annonce, setAnnonce] = useState(null);

  useEffect(() => {
    fetchAnnonce();
  }, [id]);

  const fetchAnnonce = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/annonces/${id}`);
      setAnnonce(response.data);
    } catch (error) {
      console.error('Error fetching annonce:', error);
      toast.error('Annonce non trouvee');
      navigate('/annonces');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!annonce) {
    return (
      <Layout>
        <div className="container-app py-12 text-center">
          <h1 className="text-2xl font-bold mb-4">Annonce non trouvee</h1>
          <Link to="/annonces">
            <Button>Voir toutes les annonces</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const isOwner = user?.id === annonce.user_id;

  return (
    <Layout>
      <div className="container-app py-8 md:py-12 max-w-4xl">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour
        </button>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Header Card */}
            <Card className="rounded-2xl overflow-hidden">
              {annonce.image_url && (
                <div className="w-full h-48 bg-muted">
                  <img
                    src={annonce.image_url}
                    alt={annonce.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardContent className="p-6">
                {/* Title & Meta */}
                <div className="flex items-start justify-between gap-4 mb-4">
                  <h1 className="text-2xl md:text-3xl font-bold">{annonce.title}</h1>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Eye className="w-4 h-4" />
                    <span>{annonce.views_count || 0}</span>
                  </div>
                </div>

                {/* Categories */}
                <div className="flex flex-wrap gap-2 mb-6">
                  <Badge className="bg-primary py-1.5 px-3">
                    {annonce.offer_category?.icon} Je propose: {annonce.offer_category?.name}
                  </Badge>
                  {annonce.open_to_all ? (
                    <Badge variant="secondary" className="py-1.5 px-3">
                      <Sparkles className="w-4 h-4 mr-1" />
                      Ouvert à tout type d&apos;échange
                    </Badge>
                  ) : annonce.want_categories_details?.length > 0 && (
                    <>
                      {annonce.want_categories_details.map(cat => (
                        <Badge key={cat.id} variant="secondary" className="py-1.5 px-3">
                          {cat.icon} Je cherche: {cat.name}
                        </Badge>
                      ))}
                    </>
                  )}
                </div>

                {/* Want Text */}
                {annonce.want_text && (
                  <div className="p-4 rounded-xl bg-secondary/10 mb-6">
                    <p className="text-sm font-medium text-secondary mb-1">Ce que je recherche:</p>
                    <p className="text-muted-foreground">{annonce.want_text}</p>
                  </div>
                )}

                {/* Description */}
                <div className="prose prose-sm max-w-none">
                  <h3 className="text-lg font-semibold mb-2">Description</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">{annonce.description}</p>
                </div>

                {/* Details */}
                <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Durée</p>
                      <p className="font-medium">{annonce.duration_hours}h</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Localisation</p>
                      <p className="font-medium">{annonce.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Publiee le</p>
                      <p className="font-medium">
                        {format(new Date(annonce.created_at), 'dd MMM yyyy', { locale: fr })}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* User Card */}
            <Card className="rounded-2xl">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Proposee par</CardTitle>
              </CardHeader>
              <CardContent>
                <Link to={`/users/${annonce.user?.id}`}>
                  <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors -mx-3">
                    <Avatar className="h-14 w-14">
                      <AvatarImage src={annonce.user?.avatar_url} />
                      <AvatarFallback className="bg-primary/10 text-primary text-lg">
                        {annonce.user?.full_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-1">
                        <span className="font-semibold">{annonce.user?.full_name}</span>
                        {annonce.user?.email_verified && (
                          <BadgeCheck className="w-4 h-4 text-primary" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {annonce.user?.location || annonce.location}
                      </p>
                      {annonce.user?.rating_count > 0 && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Star className="w-3 h-3 text-secondary" />
                          {annonce.user?.rating_average?.toFixed(1)} ({annonce.user?.rating_count} avis)
                        </p>
                      )}
                    </div>
                  </div>
                </Link>

                <div className="mt-4 pt-4 border-t text-center">
                  <p className="text-sm text-muted-foreground mb-1">
                    {annonce.user?.total_exchanges || 0} échange(s) réalisé(s)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Membre depuis {format(new Date(annonce.user?.created_at || annonce.created_at), 'MMMM yyyy', { locale: fr })}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Card className="rounded-2xl">
              <CardContent className="p-4 space-y-3">
                {isOwner ? (
                  <>
                    <Link to={`/annonces/${id}/matches`} className="block">
                      <Button className="w-full btn-primary" data-testid="view-matches-btn">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Voir mes matchs
                      </Button>
                    </Link>
                    <Link to={`/annonces/${id}/edit`} className="block">
                      <Button variant="outline" className="w-full">
                        Modifier l&apos;annonce
                      </Button>
                    </Link>
                  </>
                ) : isAuthenticated ? (
                  <Link to={`/annonces/${id}/propose`} className="block">
                    <Button className="w-full btn-primary h-12" data-testid="propose-exchange-btn">
                      <ArrowRightLeft className="w-5 h-5 mr-2" />
                      Proposer un échange
                    </Button>
                  </Link>
                ) : (
                  <Link to="/login" className="block">
                    <Button className="w-full btn-primary h-12">
                      Connectez-vous pour proposer un échange
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
